/**
 * The recovery request — where A7's two links go. `/auth/help` and `/auth/help?topic=number`.
 *
 * Undrawn. A7 draws "নম্বর বদলে গেছে?" and "সহায়তা নিন" as links with no destination, and a
 * closing note that promises the path "ends at a person". This is that path's first step:
 * a short form a person fills in WITHOUT a session — they are here precisely because they
 * cannot sign in — that reaches a human. Built from A3/A7's own vocabulary (the back
 * arrow, the 24px heading, the 14px field label, the 58px bordered field, the full-width
 * primary button) so it reads as part of the same flow. Its copy is authored
 * (`copy.ts § recovery`) except the two headings, which are A7's own link text.
 *
 * Built, and closed. Nothing receives the request yet, and the screen says so on arrival
 * rather than after somebody has filled it in — the next section is the whole of why.
 *
 * ── NO ROUTE RECEIVES THIS YET, AND THE SCREEN SAYS SO BEFORE IT ASKS ─────────
 *
 * The W2-12 brief names `POST /v1/support`. No Worker route serves it: `/v1/support/
 * tickets` is E4's, needs a session and an order, and writes `support_tickets`, whose
 * `identity_id` is not null — and the prefix's sign-in floor (`app.use('*',
 * requireLevel(FLOOR_LEVEL))`) answers an anonymous POST to `/v1/support` itself with a
 * 401 `no_identity` before any route is matched. Everybody who reaches this screen is
 * signed out — that is what brought them here — so the refusal is not a risk to handle: it
 * is what happens, every time, to every one of them.
 *
 * So the form is not drawn at all while that is true. `RECOVERY_REQUESTS_SERVED` is false,
 * and what renders is A7's heading, `closed_bn` — requests are not taken here yet — and a
 * way back. The reason is the intro the form carries (`copy.ts § recovery.*_intro_bn`):
 * it says a person will verify who they are and make contact, and it says it BEFORE
 * anything is typed, on the screen reached by the people who are most stuck. A promise
 * the product cannot keep at the moment it is read is worse than a closed door, and a
 * form that takes a lost number and a story and then answers that it was not taken is
 * worse again.
 *
 * The form, its send, and its reading of a refusal are kept and tested, because the
 * missing half is a Worker route and not this screen: the day the route lands the constant
 * flips and nothing else changes. `notServed` stays with it as the second guard — if the
 * route is ever withdrawn, a submission says "closed" rather than showing a success nobody
 * received, which is the fake success state the founder ruled out.
 *
 * REPORTED (`docs/deviations/W2-12.md` § what no route serves yet): a signed-out recovery
 * request needs its own table, its own abuse budget and its own Turnstile check — API and
 * migration work outside this package.
 *
 * The body below is the proposed contract, not a guess to be matched later:
 *   { topic: 'number_changed' | 'help', contact_phone_e164, old_phone_e164?, details,
 *     device_id, turnstile_token? }  →  201 { reference }
 */
import { useRef, useState } from 'react';
import { isNoIdentity } from '@oprappo/contracts';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, IconButton } from '@oprappo/ui';
import { api, ApiError, UnmappedApiError } from '../../api/client.js';
import { InlineAnswer, answerFor, isInline, noteFailure, renderAuthFailure, type AuthAnswer } from '../../auth/failure.js';
import { isPlausibleMobile, toE164 } from '../../auth/otp.js';
import { deviceId } from '../../auth/session.js';
import { ONBOARDING_COPY } from './copy.js';
import { FIELD_ERROR_BOX, FIELD_ERROR_LINE, FIELD_LABEL, Frame, SAND_FRAME } from './frame.js';
import { takeTurnstileToken, useTurnstile } from './turnstile.js';

/** Where A7's links open this screen. */
export const RECOVERY_PATH = '/auth/help';

/**
 * Does a Worker route receive a signed-out recovery request? Not today — see the header.
 *
 * The whole switch. True draws the form; false draws the closed door instead of it. Typed
 * `boolean` on purpose: this is a fact about the API that changes, not a constant of this
 * file, and the code on both sides of it is meant to keep type-checking.
 */
export const RECOVERY_REQUESTS_SERVED: boolean = false;

const WHERE = 'A7 recovery · POST /v1/support';

/** A national-format shape, never a number somebody owns. */
const PLACEHOLDER = '01XXX XXX XXX';

export type RecoveryTopic = 'number_changed' | 'help';

/** `?topic=number` is the নম্বর বদলে গেছে? link; anything else is সহায়তা নিন. */
export function topicFrom(search: string): RecoveryTopic {
  return new URLSearchParams(search).get('topic') === 'number' ? 'number_changed' : 'help';
}

export interface RecoveryRequest {
  topic: RecoveryTopic;
  contact_phone_e164: string;
  old_phone_e164?: string | undefined;
  details: string;
  challengeToken: string | null;
}

export type RecoveryResult = { outcome: 'sent'; reference: string | null } | { outcome: 'closed' };

/**
 * Did the Worker say nothing here takes an anonymous request?
 *
 * Two answers mean that today, and both are read as "closed", never as a failure of the
 * person's: the not-found envelope, and — what production actually answers, measured —
 * a 401 `no_identity` from E4's `/v1/support/*` sub-app, whose `requireLevel` floor runs
 * on every path under the prefix, this one included. A request sent with no session
 * refused for want of one is a door that is not open to the signed-out, not a problem
 * the person can fix by trying again.
 */
export function notServed(cause: unknown): boolean {
  if (cause instanceof ApiError) {
    if (cause.status === 401 && isNoIdentity(cause.body)) return true;
    return cause.status === 404 && (cause.body as { refusal?: unknown }).refusal === 'not_found';
  }
  return cause instanceof UnmappedApiError && (cause.status === 404 || cause.status === 405 || cause.status === 501);
}

/**
 * Send the request, or learn that nothing takes it yet.
 *
 * A function and not a click handler, so the bytes that leave the browser and the reading
 * of a "not served" answer are assertable without a DOM. Anonymous: whatever session this
 * browser once held is not who is asking.
 */
export async function submitRecoveryRequest(input: RecoveryRequest): Promise<RecoveryResult> {
  try {
    const body = await api.post<{ reference?: unknown } | null>(
      '/v1/support',
      {
        topic: input.topic,
        contact_phone_e164: input.contact_phone_e164,
        ...(input.old_phone_e164 === undefined ? {} : { old_phone_e164: input.old_phone_e164 }),
        details: input.details,
        device_id: deviceId(),
        ...(input.challengeToken === null ? {} : { turnstile_token: input.challengeToken }),
      },
      { anonymous: true },
    );
    const reference = typeof body?.reference === 'string' && body.reference !== '' ? body.reference : null;
    return { outcome: 'sent', reference };
  } catch (cause) {
    if (notServed(cause)) return { outcome: 'closed' };
    throw cause;
  }
}

type FieldErrors = Partial<Record<'old' | 'contact' | 'details', string>>;

function fieldClass(error: string | undefined): string {
  return (
    'w-full h-[58px] px-[15px] font-anek font-medium text-[18px] leading-[1.75] text-ink ' +
    '[font-variant-numeric:tabular-nums] outline-none rounded-none placeholder:text-muted placeholder:font-normal ' +
    (error === undefined ? 'border border-border bg-white' : FIELD_ERROR_BOX)
  );
}

/**
 * A7's heading, and the plain fact that nothing here takes a request yet.
 *
 * Rendered INSTEAD of the form, not after it, for as long as `RECOVERY_REQUESTS_SERVED`
 * is false. It promises nothing and asks for nothing; the one control is the way back to
 * the door they came from, because there is nothing else here to do.
 */
function RecoveryClosed({ topic }: { topic: RecoveryTopic }): JSX.Element {
  const navigate = useNavigate();
  const copy = ONBOARDING_COPY.recovery;
  return (
    <Frame className={`${SAND_FRAME} px-[24px] pt-[20px] pb-[24px]`}>
      <div className="flex">
        <IconButton control="back" size={22} onClick={() => navigate(-1)} />
      </div>

      <div className="mt-[30px]">
        <h1 className="m-0 font-anek font-semibold text-[24px] leading-[1.25] text-ink">
          {topic === 'number_changed' ? copy.number_title_bn : copy.help_title_bn}
        </h1>
        <div
          role="status"
          data-recovery="closed"
          data-copy-status={ONBOARDING_COPY.copy_status}
          className="bg-warning-surface border border-warning-border px-[15px] py-[12px] mt-[16px] font-anek font-normal text-[14px] leading-[1.75] text-ink2"
        >
          {copy.closed_bn}
        </div>
      </div>

      <div className="flex-1" />

      <Button tone="primary" size="lg" onClick={() => navigate('/auth/login')}>
        {copy.done_bn}
      </Button>
    </Frame>
  );
}

/** A7's two links, answered by whichever of the two screens is honest today. */
export function RecoveryScreen(): JSX.Element {
  const location = useLocation();
  const topic = topicFrom(location.search);
  return RECOVERY_REQUESTS_SERVED ? <RecoveryForm topic={topic} /> : <RecoveryClosed topic={topic} />;
}

/**
 * The request itself — reachable only once a route receives it. See the header.
 *
 * Exported for the test that keeps it ready: while `RECOVERY_REQUESTS_SERVED` is false
 * nothing renders this, and an untested screen is one that has quietly stopped working by
 * the day somebody flips the constant.
 */
export function RecoveryForm({ topic }: { topic: RecoveryTopic }): JSX.Element {
  const navigate = useNavigate();
  const copy = ONBOARDING_COPY.recovery;

  const [oldNumber, setOldNumber] = useState('');
  const [contact, setContact] = useState('');
  const [details, setDetails] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [answer, setAnswer] = useState<AuthAnswer | null>(null);
  const [shown, setShown] = useState<unknown>(null);
  const [result, setResult] = useState<RecoveryResult | null>(null);
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const challenge = useRef<HTMLDivElement>(null);

  const frame =
    shown === null ? null : renderAuthFailure(shown, { onRetry: () => setShown(null), onBack: () => setShown(null) });
  const sent = result?.outcome === 'sent';
  const turnstile = useTurnstile(challenge, frame === null && !sent);

  const submit = async (): Promise<void> => {
    if (inFlight.current) return;
    const found: FieldErrors = {};
    const contactE164 = toE164(contact);
    if (contactE164 === null || !isPlausibleMobile(contactE164)) found.contact = ONBOARDING_COPY.door.number_invalid_bn;
    const oldE164 = topic === 'number_changed' ? toE164(oldNumber) : null;
    if (topic === 'number_changed' && (oldE164 === null || !isPlausibleMobile(oldE164))) {
      found.old = ONBOARDING_COPY.door.number_invalid_bn;
    }
    if (topic === 'help' && details.trim() === '') found.details = copy.details_required_bn;
    setErrors(found);
    if (Object.keys(found).length > 0 || contactE164 === null) return;

    inFlight.current = true;
    setBusy(true);
    setAnswer(null);
    try {
      setResult(
        await submitRecoveryRequest({
          topic,
          contact_phone_e164: contactE164,
          ...(oldE164 === null ? {} : { old_phone_e164: oldE164 }),
          details: details.trim(),
          challengeToken: takeTurnstileToken(),
        }),
      );
    } catch (cause) {
      const said = answerFor(noteFailure(WHERE, cause));
      if (isInline(said)) setAnswer(said);
      else setShown(cause);
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  if (frame !== null) return frame;

  if (result?.outcome === 'sent') {
    return (
      <Frame className={`${SAND_FRAME} px-[24px] pt-[20px] pb-[24px]`}>
        <div data-recovery="sent" data-copy-status={ONBOARDING_COPY.copy_status} className="mt-[52px]">
          <h1 className="m-0 font-anek font-semibold text-[24px] leading-[1.25] text-ink">{copy.sent_title_bn}</h1>
          <div role="status" className="font-anek font-normal text-[15px] leading-[1.75] text-body mt-[7px]">
            {copy.sent_body_bn}
          </div>
          {/* Latin, Manrope — the string a person reads aloud to support, as § ৫ sets it. */}
          {result.reference === null ? null : (
            <div className="font-manrope font-medium text-[13px] leading-[1.5] text-muted mt-[12px]">
              {result.reference}
            </div>
          )}
        </div>
        <div className="flex-1" />
        <Button tone="primary" size="lg" onClick={() => navigate('/auth/login')}>
          {copy.done_bn}
        </Button>
      </Frame>
    );
  }

  return (
    <Frame className={`${SAND_FRAME} px-[24px] pt-[20px] pb-[24px] overflow-y-auto`}>
      <div className="flex">
        <IconButton control="back" size={22} onClick={() => navigate(-1)} />
      </div>

      <div className="mt-[30px]" data-recovery={topic}>
        <h1 className="m-0 font-anek font-semibold text-[24px] leading-[1.25] text-ink">
          {topic === 'number_changed' ? copy.number_title_bn : copy.help_title_bn}
        </h1>
        <div
          data-copy-status={ONBOARDING_COPY.copy_status}
          className="font-anek font-normal text-[15px] leading-[1.75] text-body mt-[7px]"
        >
          {topic === 'number_changed' ? copy.number_intro_bn : copy.help_intro_bn}
        </div>
      </div>

      <div data-copy-status={ONBOARDING_COPY.copy_status} className="mt-[24px] flex flex-col gap-[18px]">
        {topic === 'number_changed' ? (
          <div>
            <label htmlFor="recovery-old" className={FIELD_LABEL}>
              {copy.old_number_label_bn}
            </label>
            <input
              id="recovery-old"
              type="tel"
              inputMode="tel"
              autoFocus
              value={oldNumber}
              placeholder={PLACEHOLDER}
              aria-invalid={errors.old !== undefined ? true : undefined}
              onChange={(e) => setOldNumber(e.target.value)}
              className={fieldClass(errors.old)}
            />
            {errors.old === undefined ? null : <div role="alert" className={FIELD_ERROR_LINE + ' mt-[6px]'}>{errors.old}</div>}
          </div>
        ) : null}

        <div>
          <label htmlFor="recovery-contact" className={FIELD_LABEL}>
            {copy.contact_label_bn}
          </label>
          <input
            id="recovery-contact"
            type="tel"
            inputMode="tel"
            autoComplete="tel-national"
            autoFocus={topic === 'help'}
            value={contact}
            placeholder={PLACEHOLDER}
            aria-invalid={errors.contact !== undefined ? true : undefined}
            onChange={(e) => setContact(e.target.value)}
            className={fieldClass(errors.contact)}
          />
          {errors.contact === undefined ? null : (
            <div role="alert" className={FIELD_ERROR_LINE + ' mt-[6px]'}>{errors.contact}</div>
          )}
        </div>

        <div>
          <label htmlFor="recovery-details" className={FIELD_LABEL}>
            {copy.details_label_bn}
          </label>
          <textarea
            id="recovery-details"
            value={details}
            rows={4}
            maxLength={1000}
            aria-invalid={errors.details !== undefined ? true : undefined}
            onChange={(e) => setDetails(e.target.value)}
            className={
              'w-full min-h-[112px] px-[15px] py-[12px] font-anek font-normal text-[16px] leading-[1.75] text-ink ' +
              'outline-none rounded-none resize-none ' +
              (errors.details === undefined ? 'border border-border bg-white' : FIELD_ERROR_BOX)
            }
          />
          {errors.details === undefined ? (
            <div className="font-anek font-normal text-[13px] leading-[1.75] text-muted mt-[6px]">{copy.details_hint_bn}</div>
          ) : (
            <div role="alert" className={FIELD_ERROR_LINE + ' mt-[6px]'}>{errors.details}</div>
          )}
        </div>
      </div>

      <div className="flex-1 min-h-[20px]" />

      <div ref={challenge} className="empty:hidden flex" />

      {/* The second guard: the route was withdrawn between the flip and this press. */}
      {result?.outcome === 'closed' ? (
        <div
          role="status"
          data-recovery="closed"
          data-copy-status={ONBOARDING_COPY.copy_status}
          className="bg-warning-surface border border-warning-border px-[15px] py-[12px] mb-[12px] font-anek font-normal text-[14px] leading-[1.75] text-ink2"
        >
          {copy.closed_bn}
        </div>
      ) : null}

      <InlineAnswer answer={answer} unexplainedBn={copy.not_sent_bn} className="mb-[10px]" />

      <Button tone="primary" size="lg" disabled={busy || !turnstile.ready} onClick={() => void submit()}>
        {copy.submit_bn}
      </Button>
    </Frame>
  );
}
